/**
 * StepCharacterSelection Component
 *
 * First step of the story creation wizard.
 * Allows users to select main characters (max 2) and supporting characters (max 3).
 */

import { useState } from 'react'

import { useAccountCharactersList } from '@/shared/api/generated/characters/characters'
import { CharacterList } from '@/shared/api/models'
import { Button } from '@/shared/components/Button/Button'
import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { AddNewCharacterCard, CharacterCard } from '@/slices/Shop/PersonalizationWizard/CharacterCard'
import { InlineCharacterForm } from '@/slices/Shop/PersonalizationWizard/InlineCharacterForm'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { Alert, Box, CircularProgress, Grid2 as Grid, styled, Typography } from '@mui/material'

import { SelectedCharacters } from '../types'

interface StepCharacterSelectionProps {
  selectedCharacters: SelectedCharacters
  onSelect: (selected: SelectedCharacters) => void
  onContinue: () => void
}

const MAX_MAIN_CHARACTERS = 2
const MAX_SUPPORTING_CHARACTERS = 3

export const StepCharacterSelection: React.FC<StepCharacterSelectionProps> = ({
  selectedCharacters,
  onSelect,
  onContinue,
}) => {
  const [showForm, setShowForm] = useState(false)

  const { data: characters, isLoading, error } = useAccountCharactersList()

  const activeCharacters = characters?.filter((c) => c.is_active) || []
  const { mainCharacterIds, supportingCharacterIds } = selectedCharacters

  const handleToggleMainCharacter = (character: CharacterList) => {
    const isSelected = mainCharacterIds.includes(character.id)

    if (isSelected) {
      onSelect({
        ...selectedCharacters,
        mainCharacterIds: mainCharacterIds.filter((id) => id !== character.id),
      })
    } else if (mainCharacterIds.length < MAX_MAIN_CHARACTERS) {
      // Remove from supporting if already there
      onSelect({
        mainCharacterIds: [...mainCharacterIds, character.id],
        supportingCharacterIds: supportingCharacterIds.filter((id) => id !== character.id),
      })
    }
  }

  const handleToggleSupportingCharacter = (character: CharacterList) => {
    const isSelected = supportingCharacterIds.includes(character.id)

    if (isSelected) {
      onSelect({
        ...selectedCharacters,
        supportingCharacterIds: supportingCharacterIds.filter((id) => id !== character.id),
      })
    } else if (supportingCharacterIds.length < MAX_SUPPORTING_CHARACTERS) {
      // Remove from main if already there
      onSelect({
        mainCharacterIds: mainCharacterIds.filter((id) => id !== character.id),
        supportingCharacterIds: [...supportingCharacterIds, character.id],
      })
    }
  }

  const handleAddNewClick = () => {
    setShowForm(true)
  }

  const handleFormBack = () => {
    setShowForm(false)
  }

  const handleFormSuccess = (characterId: number) => {
    setShowForm(false)

    // Add new character to main if room, else supporting
    if (mainCharacterIds.length < MAX_MAIN_CHARACTERS) {
      onSelect({
        ...selectedCharacters,
        mainCharacterIds: [...mainCharacterIds, characterId],
      })
    } else if (supportingCharacterIds.length < MAX_SUPPORTING_CHARACTERS) {
      onSelect({
        ...selectedCharacters,
        supportingCharacterIds: [...supportingCharacterIds, characterId],
      })
    }
  }

  const canContinue = mainCharacterIds.length > 0
  const totalSelected = mainCharacterIds.length + supportingCharacterIds.length

  if (showForm) {
    return <InlineCharacterForm onBack={handleFormBack} onSuccess={handleFormSuccess} />
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <Typography variant="h4" component="h2" gutterBottom>
          Choose Your Story Heroes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select the main characters and supporting characters for your story
        </Typography>
      </StyledHeader>

      {isLoading ? (
        <StyledLoadingContainer>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading your characters...
          </Typography>
        </StyledLoadingContainer>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load characters. Please try again.
        </Alert>
      ) : activeCharacters.length === 0 ? (
        <StyledEmptyState>
          <Typography variant="h6" gutterBottom>
            No Characters Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a character by uploading a photo of your child. The AI will generate
            a personalized avatar that appears throughout the story.
          </Typography>
          <Button primary onClick={handleAddNewClick}>
            Create Your First Character
          </Button>
        </StyledEmptyState>
      ) : (
        <>
          {/* Main Characters Section */}
          <StyledSection>
            <StyledSectionHeader>
              <Typography variant="h6">
                Main Characters ({mainCharacterIds.length}/{MAX_MAIN_CHARACTERS})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                These characters will be the stars of the story
              </Typography>
            </StyledSectionHeader>

            <Grid container spacing={2}>
              {activeCharacters.map((character) => {
                const isMainSelected = mainCharacterIds.includes(character.id)
                const isSupportingSelected = supportingCharacterIds.includes(character.id)

                return (
                  <Grid key={character.id} size={{ xs: 6, sm: 4, md: 3 }}>
                    <CharacterCard
                      character={character}
                      isSelected={isMainSelected}
                      onClick={() => handleToggleMainCharacter(character)}
                      disabled={
                        isSupportingSelected ||
                        (!isMainSelected && mainCharacterIds.length >= MAX_MAIN_CHARACTERS)
                      }
                    />
                  </Grid>
                )
              })}
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <AddNewCharacterCard onClick={handleAddNewClick} />
              </Grid>
            </Grid>
          </StyledSection>

          {/* Supporting Characters Section */}
          <StyledSection>
            <StyledSectionHeader>
              <Typography variant="h6">
                Supporting Characters ({supportingCharacterIds.length}/{MAX_SUPPORTING_CHARACTERS})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optional - These characters will appear in supporting roles
              </Typography>
            </StyledSectionHeader>

            <Grid container spacing={2}>
              {activeCharacters.map((character) => {
                const isMainSelected = mainCharacterIds.includes(character.id)
                const isSupportingSelected = supportingCharacterIds.includes(character.id)

                return (
                  <Grid key={character.id} size={{ xs: 6, sm: 4, md: 3 }}>
                    <CharacterCard
                      character={character}
                      isSelected={isSupportingSelected}
                      onClick={() => handleToggleSupportingCharacter(character)}
                      disabled={
                        isMainSelected ||
                        (!isSupportingSelected &&
                          supportingCharacterIds.length >= MAX_SUPPORTING_CHARACTERS)
                      }
                    />
                  </Grid>
                )
              })}
            </Grid>
          </StyledSection>

          {/* Summary and Continue */}
          <StyledFooter>
            {totalSelected > 0 && (
              <StyledSummary>
                <Typography variant="body2">
                  <strong>{mainCharacterIds.length}</strong> main character
                  {mainCharacterIds.length !== 1 ? 's' : ''}
                  {supportingCharacterIds.length > 0 && (
                    <>
                      {' '}
                      and <strong>{supportingCharacterIds.length}</strong> supporting character
                      {supportingCharacterIds.length !== 1 ? 's' : ''}
                    </>
                  )}{' '}
                  selected
                </Typography>
              </StyledSummary>
            )}

            <Button
              primary
              size="large"
              onClick={onContinue}
              disabled={!canContinue}
              sx={{ minWidth: pxToRem(200) }}
            >
              Continue to Plot
              <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
            </Button>

            {!canContinue && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select at least one main character to continue
              </Typography>
            )}
          </StyledFooter>
        </>
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)`
  max-width: ${pxToRem(900)};
  margin: 0 auto;
`

const StyledHeader = styled(Box)`
  text-align: center;
  margin-bottom: ${pxToRem(32)};
`

const StyledLoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${pxToRem(300)};
`

const StyledEmptyState = styled(Box)`
  text-align: center;
  padding: ${pxToRem(48)} ${pxToRem(24)};
  background-color: ${(props) => props.theme.palette.background.paper};
  border-radius: ${pxToRem(16)};
`

const StyledSection = styled(Box)`
  margin-bottom: ${pxToRem(32)};
`

const StyledSectionHeader = styled(Box)`
  margin-bottom: ${pxToRem(16)};
`

const StyledFooter = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: ${pxToRem(24)};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledSummary = styled(Box)`
  padding: ${pxToRem(8)} ${pxToRem(16)};
  background-color: ${(props) => props.theme.palette.info.light}20;
  border-radius: ${pxToRem(8)};
  margin-bottom: ${pxToRem(16)};
`

export default StepCharacterSelection
